/**
 * Sponsors & Leads — sample CRM dataset (front-end demonstration only).
 *
 * Nothing here is persisted. No record is read from or written to the Backend.
 * Regions and packages reuse the existing revenue-demo model so lead targeting
 * lines up with the rest of the dashboard.
 */

import type { Region } from '@/pages/demo/revenue/components/revenueData';

export type LeadStage = 'New inquiry' | 'Qualified' | 'Proposal sent' | 'Negotiation' | 'Won' | 'Lost';

export const LEAD_STAGES: LeadStage[] = ['New inquiry', 'Qualified', 'Proposal sent', 'Negotiation', 'Won', 'Lost'];

export const OPEN_STAGES: LeadStage[] = ['New inquiry', 'Qualified', 'Proposal sent', 'Negotiation'];

export type LeadSource = 'Website form' | 'Advertise page' | 'Referral' | 'Outbound' | 'Event';

export const LEAD_SOURCES: LeadSource[] = ['Website form', 'Advertise page', 'Referral', 'Outbound', 'Event'];

export type BadgeTone = 'live' | 'scheduled' | 'review' | 'draft' | 'done' | 'alert' | 'neutral';

export const STAGE_TONE: Record<LeadStage, BadgeTone> = {
  'New inquiry': 'review',
  Qualified: 'scheduled',
  'Proposal sent': 'neutral',
  Negotiation: 'alert',
  Won: 'live',
  Lost: 'draft',
};

export type LeadActivityKind = 'email' | 'call' | 'note' | 'meeting' | 'stage';

export type LeadActivity = { id: string; kind: LeadActivityKind; text: string; at: string };

export type LeadTask = { id: string; label: string; due: string; done: boolean };

export type Lead = {
  id: string;
  company: string;
  contact: string;
  title: string;
  email: string;
  phone: string;
  stage: LeadStage;
  source: LeadSource;
  regionIds: string[];
  packageId: string | null; // null => presenting / custom quote, undecided
  valueMonthly: number | null; // null => custom quote
  owner: string;
  createdAt: string;
  lastTouch: string;
  nextStep: string;
  notes: string;
  activity: LeadActivity[];
  tasks: LeadTask[];
};

export const OWNERS = ['Clint', 'Art', 'Dana'];

export const LEADS: Lead[] = [
  {
    id: 'ld-01',
    company: 'Harbor Point Grill',
    contact: 'Dana Whitfield',
    title: 'Marketing Lead',
    email: 'dana@harborpoint.example.com',
    phone: '562-555-0114',
    stage: 'New inquiry',
    source: 'Website form',
    regionIds: ['los-angeles'],
    packageId: 'featured',
    valueMonthly: 299,
    owner: 'Art',
    createdAt: '2026-10-08',
    lastTouch: '2026-10-08',
    nextStep: '2026-10-10',
    notes: 'Weekend brunch spot near the course. Wants to reach LA weekend players; budget flexible.',
    activity: [
      { id: 'a1', kind: 'stage', text: 'Lead created from the public Advertise form', at: '2026-10-08' },
      { id: 'a2', kind: 'email', text: 'Sent introductory email with the Featured Sponsor overview', at: '2026-10-08' },
    ],
    tasks: [
      { id: 't1', label: 'Call to confirm package interest', due: '2026-10-10', done: false },
      { id: 't2', label: 'Send region reach figures for LA', due: '2026-10-11', done: false },
    ],
  },
  {
    id: 'ld-02',
    company: 'East Valley Auto',
    contact: 'Marcus Ruiz',
    title: 'Owner',
    email: 'marcus@eastvalleyauto.example.com',
    phone: '602-555-0177',
    stage: 'New inquiry',
    source: 'Referral',
    regionIds: ['phoenix'],
    packageId: 'local',
    valueMonthly: 149,
    owner: 'Clint',
    createdAt: '2026-10-07',
    lastTouch: '2026-10-07',
    nextStep: '2026-10-11',
    notes: 'Referred by Desert Sky Golf. First-time advertiser — walk through how placement works.',
    activity: [
      { id: 'a1', kind: 'stage', text: 'Lead created from a partner referral', at: '2026-10-07' },
      { id: 'a2', kind: 'note', text: 'Referral from Desert Sky Golf — warm introduction expected', at: '2026-10-07' },
    ],
    tasks: [{ id: 't1', label: 'Send Local Sponsor one-pager', due: '2026-10-11', done: false }],
  },
  {
    id: 'ld-03',
    company: 'Coastal Credit Union',
    contact: 'Priya Raman',
    title: 'Regional Marketing Director',
    email: 'priya@coastalcu.example.com',
    phone: '619-555-0142',
    stage: 'Qualified',
    source: 'Advertise page',
    regionIds: ['san-diego'],
    packageId: 'regional',
    valueMonthly: 599,
    owner: 'Art',
    createdAt: '2026-10-04',
    lastTouch: '2026-10-08',
    nextStep: '2026-10-12',
    notes: 'Wants San Diego category exclusivity. Confirmed budget. Needs creative ready by end of month.',
    activity: [
      { id: 'a1', kind: 'stage', text: 'Lead created from the Advertise page', at: '2026-10-04' },
      { id: 'a2', kind: 'call', text: 'Qualifying call — confirmed budget and Feb campaign start', at: '2026-10-06' },
      { id: 'a3', kind: 'email', text: 'Shared Regional Partner package details and SD reach', at: '2026-10-08' },
    ],
    tasks: [
      { id: 't1', label: 'Prepare custom proposal with exclusivity add-on', due: '2026-10-12', done: false },
      { id: 't2', label: 'Confirm creative deadline', due: '2026-10-13', done: false },
    ],
  },
  {
    id: 'ld-04',
    company: 'Sonoran Cactus Club',
    contact: 'Teresa Holbrook',
    title: 'Events Coordinator',
    email: 'teresa@sonorancactus.example.com',
    phone: '520-555-0163',
    stage: 'Qualified',
    source: 'Website form',
    regionIds: ['tucson', 'phoenix'],
    packageId: 'featured',
    valueMonthly: 299,
    owner: 'Dana',
    createdAt: '2026-10-03',
    lastTouch: '2026-10-07',
    nextStep: '2026-10-11',
    notes: 'Interested in two markets — Tucson primary, Phoenix secondary. Asked about barter options.',
    activity: [
      { id: 'a1', kind: 'stage', text: 'Lead created from the public Advertise form', at: '2026-10-03' },
      { id: 'a2', kind: 'email', text: 'Explained two-region targeting and barter policy', at: '2026-10-07' },
    ],
    tasks: [{ id: 't1', label: 'Draft two-region Featured package quote', due: '2026-10-11', done: false }],
  },
  {
    id: 'ld-05',
    company: 'IE Auto Plaza',
    contact: 'Victor Alvarez',
    title: 'General Manager',
    email: 'victor@ieautoplaza.example.com',
    phone: '951-555-0139',
    stage: 'Proposal sent',
    source: 'Outbound',
    regionIds: ['inland-empire'],
    packageId: 'featured',
    valueMonthly: 299,
    owner: 'Clint',
    createdAt: '2026-09-29',
    lastTouch: '2026-10-06',
    nextStep: '2026-10-13',
    notes: 'Proposal sent for the Inland Empire Featured placement. Waiting on their Q4 sign-off.',
    activity: [
      { id: 'a1', kind: 'stage', text: 'Lead created from an outbound prospecting call', at: '2026-09-29' },
      { id: 'a2', kind: 'meeting', text: 'Intro meeting — reviewed Inland Empire reach', at: '2026-10-02' },
      { id: 'a3', kind: 'email', text: 'Sent formal Featured Sponsor proposal', at: '2026-10-06' },
    ],
    tasks: [{ id: 't1', label: 'Follow up on Q4 approval', due: '2026-10-13', done: false }],
  },
  {
    id: 'ld-06',
    company: 'Torrey Pines Grill',
    contact: 'Lauren Chen',
    title: 'Partner Relations',
    email: 'lauren@torreypinesgrill.example.com',
    phone: '858-555-0121',
    stage: 'Proposal sent',
    source: 'Event',
    regionIds: ['san-diego'],
    packageId: 'featured',
    valueMonthly: 299,
    owner: 'Art',
    createdAt: '2026-09-30',
    lastTouch: '2026-10-07',
    nextStep: '2026-10-14',
    notes: 'Met at the Coastal Amateur Open. Proposal sent; they want a March start alongside the event.',
    activity: [
      { id: 'a1', kind: 'stage', text: 'Lead created at the Coastal Amateur Open booth', at: '2026-09-30' },
      { id: 'a2', kind: 'email', text: 'Sent Featured Sponsor proposal tied to the March event', at: '2026-10-07' },
    ],
    tasks: [{ id: 't1', label: 'Confirm March start date', due: '2026-10-14', done: false }],
  },
  {
    id: 'ld-07',
    company: 'Desert Sky Golf',
    contact: 'Aaron Feldman',
    title: 'Club Manager',
    email: 'aaron@desertskygolf.example.com',
    phone: '480-555-0198',
    stage: 'Negotiation',
    source: 'Referral',
    regionIds: ['phoenix'],
    packageId: 'regional',
    valueMonthly: 599,
    owner: 'Clint',
    createdAt: '2026-09-24',
    lastTouch: '2026-10-08',
    nextStep: '2026-10-09',
    notes: 'Negotiating a 6-month commitment in exchange for a rate hold. Verbal yes to Phoenix exclusivity.',
    activity: [
      { id: 'a1', kind: 'stage', text: 'Lead created from a member referral', at: '2026-09-24' },
      { id: 'a2', kind: 'call', text: 'Pricing call — discussed 6-month commitment', at: '2026-10-01' },
      { id: 'a3', kind: 'note', text: 'Verbal agreement on Phoenix exclusivity', at: '2026-10-08' },
    ],
    tasks: [
      { id: 't1', label: 'Send 6-month rate-hold agreement', due: '2026-10-09', done: false },
      { id: 't2', label: 'Line up creative kickoff', due: '2026-10-15', done: false },
    ],
  },
  {
    id: 'ld-08',
    company: 'Pacific Links Realty',
    contact: 'Grace Nakamura',
    title: 'Managing Broker',
    email: 'grace@pacificlinks.example.com',
    phone: '310-555-0155',
    stage: 'Negotiation',
    source: 'Website form',
    regionIds: ['los-angeles', 'orange-county'],
    packageId: 'regional',
    valueMonthly: 599,
    owner: 'Art',
    createdAt: '2026-09-22',
    lastTouch: '2026-10-08',
    nextStep: '2026-10-12',
    notes: 'Wants LA + OC together at the Regional rate. Reviewing the quarterly spotlight story add-on.',
    activity: [
      { id: 'a1', kind: 'stage', text: 'Lead created from the public Advertise form', at: '2026-09-22' },
      { id: 'a2', kind: 'email', text: 'Sent combined LA + OC proposal', at: '2026-10-05' },
      { id: 'a3', kind: 'call', text: 'Reviewed spotlight story add-on over the phone', at: '2026-10-08' },
    ],
    tasks: [{ id: 't1', label: 'Confirm spotlight story scope', due: '2026-10-12', done: false }],
  },
  {
    id: 'ld-09',
    company: 'OC Mortgage Group',
    contact: 'Brian Okafor',
    title: 'Marketing Manager',
    email: 'brian@ocmortgage.example.com',
    phone: '714-555-0173',
    stage: 'Won',
    source: 'Advertise page',
    regionIds: ['orange-county'],
    packageId: 'regional',
    valueMonthly: 599,
    owner: 'Dana',
    createdAt: '2026-09-10',
    lastTouch: '2026-10-02',
    nextStep: '2026-10-20',
    notes: 'Active campaign. Renews in 5 days — needs a renewal nudge.',
    activity: [
      { id: 'a1', kind: 'stage', text: 'Lead created from the Advertise page', at: '2026-09-10' },
      { id: 'a2', kind: 'stage', text: 'Converted to an active Regional Partner campaign', at: '2026-10-02' },
    ],
    tasks: [{ id: 't1', label: 'Send renewal reminder', due: '2026-10-14', done: false }],
  },
  {
    id: 'ld-10',
    company: 'Sunset Motors',
    contact: 'Elena Vasquez',
    title: 'Dealer Principal',
    email: 'elena@sunsetmotors.example.com',
    phone: '323-555-0188',
    stage: 'Won',
    source: 'Outbound',
    regionIds: ['los-angeles'],
    packageId: 'featured',
    valueMonthly: 299,
    owner: 'Clint',
    createdAt: '2026-09-05',
    lastTouch: '2026-09-30',
    nextStep: '2026-10-18',
    notes: 'Active Featured Sponsor. Strong performance — good candidate for an upsell conversation.',
    activity: [
      { id: 'a1', kind: 'stage', text: 'Lead created from an outbound call', at: '2026-09-05' },
      { id: 'a2', kind: 'stage', text: 'Converted to an active Featured campaign', at: '2026-09-30' },
    ],
    tasks: [{ id: 't1', label: 'Pitch regional upsell on next review', due: '2026-10-18', done: false }],
  },
  {
    id: 'ld-11',
    company: 'Corona Sports Bar',
    contact: 'Tony Marchetti',
    title: 'Owner',
    email: 'tony@coronasportsbar.example.com',
    phone: '951-555-0120',
    stage: 'Lost',
    source: 'Website form',
    regionIds: ['inland-empire'],
    packageId: 'local',
    valueMonthly: 149,
    owner: 'Dana',
    createdAt: '2026-09-12',
    lastTouch: '2026-09-26',
    nextStep: '',
    notes: 'Chose to wait until the new year. Good candidate to re-open in January.',
    activity: [
      { id: 'a1', kind: 'stage', text: 'Lead created from the public Advertise form', at: '2026-09-12' },
      { id: 'a2', kind: 'email', text: 'Followed up on the Local Sponsor package', at: '2026-09-24' },
      { id: 'a3', kind: 'stage', text: 'Marked Lost — revisiting in January', at: '2026-09-26' },
    ],
    tasks: [{ id: 't1', label: 'Re-open conversation in January', due: '2027-01-08', done: false }],
  },
  {
    id: 'ld-12',
    company: 'Phoenix Trophy Co.',
    contact: 'Nadia Sterling',
    title: 'Founder',
    email: 'nadia@phxtrophy.example.com',
    phone: '602-555-0141',
    stage: 'New inquiry',
    source: 'Event',
    regionIds: ['phoenix', 'tucson'],
    packageId: null,
    valueMonthly: null,
    owner: 'Art',
    createdAt: '2026-10-09',
    lastTouch: '2026-10-09',
    nextStep: '2026-10-12',
    notes: 'Met at the Desert Match Play. Interested in a presenting-level, all-Arizona partnership — needs a custom quote.',
    activity: [
      { id: 'a1', kind: 'stage', text: 'Lead created at the Desert Match Play event', at: '2026-10-09' },
      { id: 'a2', kind: 'note', text: 'Exploring a custom presenting-level quote for Arizona', at: '2026-10-09' },
    ],
    tasks: [{ id: 't1', label: 'Scope a custom presenting proposal', due: '2026-10-12', done: false }],
  },
];

export function isOpen(lead: Lead): boolean {
  return OPEN_STAGES.includes(lead.stage);
}

export function leadValueLabel(lead: Lead): string {
  if (lead.valueMonthly === null) return 'Custom quote';
  return `$${lead.valueMonthly.toLocaleString('en-US')}/mo`;
}

export function pipelineValue(leads: Lead[]): number {
  return leads.filter(isOpen).reduce((sum, l) => sum + (l.valueMonthly ?? 0), 0);
}

export function formatDate(iso: string): string {
  if (!iso) return '—';
  const d = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function regionLabels(regionIds: string[], regionById: Record<string, Region>): string {
  return regionIds.map((id) => regionById[id]?.city ?? id).join(', ');
}

export const ACTIVITY_ICON: Record<LeadActivityKind, string> = {
  email: 'ri-mail-line',
  call: 'ri-phone-line',
  note: 'ri-sticky-note-line',
  meeting: 'ri-calendar-event-line',
  stage: 'ri-flow-chart',
};